import { Subject } from 'rxjs';
import Logger from '../utilities/logger';
import { shallowDiff } from '../utilities/object';
/**
 * Log the current value of a state object/value to the console
 *
 * @param state The state object to be logged
 * @param message An optional message to output for the log
 */
const log = (state, message = '') => {
    // Build tools will remove this code automatically for production environments
    Logger.group(message, Logger.table.bind(null, state));
};
/**
 * Update the state for a specific state value in the states array
 *
 * @param newState Object to set the state to
 * @param index Unique identifier for the state in the states array
 */
const updateState = (state, newState, moduleName, isLoggingEnabled) => {
    const combinedState = {
        ...state,
        ...newState,
    };
    // If logging is enabled, then log the updated state
    if (isLoggingEnabled) {
        log(state, `Updated state for ${moduleName})`);
    }
    return combinedState;
};
/**
 * Sets the initial state and returns functions to get and set the state
 *
 * @param  initialState An object representing the initial state
 * @param moduleName The name of the module the state is linked to. This will be used mostly at a later date.
 * @param stateChangeCallback Whether the return object should return an RxJS Subject for observing state changes
 * @param logging If state changes should be logged to the console for debugging purposes
 *
 * @return Functions for getting (getState) and setting (setState) state
 */
export const initState = ({ initialState, moduleName, stateChangeCallback, isLoggingEnabled, }) => {
    let state = initialState;
    let subject;
    // If logging is enabled, then log the new state
    if (isLoggingEnabled) {
        log(state, `Added state for ${moduleName}`);
    }
    if (typeof stateChangeCallback === 'function') {
        subject = new Subject();
        subject.subscribe(stateChangeCallback);
    }
    return {
        /**
             * Fetch the state object
             *
             * @return The state object
             */
        getState: () => Object.freeze(state),
        /**
             * Update the state value
             *
             * @param state The old state object being modified
             * @param newState An updated state value to be set
             *
             */
        setState: (newState) => {
            let oldState;
            if (typeof subject !== 'undefined') {
                oldState = { ...state };
            }
            state = updateState(state, newState, moduleName, isLoggingEnabled);
            if (typeof subject !== 'undefined') {
                subject.next(shallowDiff(oldState, newState));
            }
        },
    };
};
